import { Button, Dialog } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { post_Refund } from '../../../services/refund';
import useAuth from '../../../components/hooks/useAuth';
import { branchTimezone } from '../../../utils/summaryCalculation';
import { useTheme } from '../../../components/hooks/useTheme';
import { validateMin } from '../../../utils/validateMin';
import useConfirm from '../../../components/hooks/useConfirm';
import colorConfigs from '../../../configs/colorConfigs';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';

type SettledOutstandingBalanceProps = {
    open: boolean;
    onClose: () => void;
    formattedCreatedAt: string;
    summaryData: any;
    reservationId: number;
    refreshTheReservation: () => void;
    setPleaseWait: (value: boolean) => void;
    refundList: any[];
    allPaymentList: any[];
}

const SettledOutstandingBalance = ({
    open,
    onClose,
    formattedCreatedAt,
    summaryData,
    reservationId,
    refreshTheReservation,
    setPleaseWait,
    refundList,
    allPaymentList
}: SettledOutstandingBalanceProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [actualRefundAmount, setActualRefundAmount] = useState<number>(0.0);
    const [amount, setAmount] = useState<number>(0.0);
    const UpdateTimezone = branchTimezone(formattedCreatedAt);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (open) {
            if (summaryData) {
                let totalRefundSecurityAmount = refundList?.reduce((sum, refund) => {
                    return refund?.refundType === "Security Deposit" ? sum + refund?.amount : sum;
                }, 0) || 0;

                const securityDepositAmount = (totalRefundSecurityAmount >= summaryData?.securityDepositAmountPaid)
                    ? 0.0
                    : (summaryData?.securityDepositAmountPaid - totalRefundSecurityAmount);

                const outstandingBalance = summaryData?.outstandingBalance || 0;
                const amountToRefund = outstandingBalance > securityDepositAmount
                    ? 0
                    : (securityDepositAmount - outstandingBalance)
                    ;

                setAmount(Number(amountToRefund?.toFixed(2)));
                setActualRefundAmount(Number(securityDepositAmount?.toFixed(2)));
            } else {
                setAmount(0.0);
                setActualRefundAmount(0.0);
            }
        } else {
            setAmount(0.0);
            setActualRefundAmount(0.0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWait(true);

        if (amount > actualRefundAmount) {
            setConfirmData({
                title: 'Warning',
                summary: "Refund amount cannot exceed the security deposit amount to refund.",
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
            setPleaseWait(false);
            return;
        }

        if (amount >= 0) {
            const paymentReference = allPaymentList?.find((item: any) => item?.paymentType === "Security Deposit" && item?.status === "Approved");

            await post_Refund({
                reservationId: reservationId,
                refundType: "Security Deposit",
                dateofRefund: new Date(UpdateTimezone).toLocaleString(),
                amount: amount,
                reference: paymentReference?.reference || "",
                createdAt: new Date(UpdateTimezone).toLocaleString(),
                createdBy: auth.givenname,
                syncedWithAccounting: false,
                refundMethod: paymentReference?.paymentMethod || "Credit Card",
                refundStatus: ""
            }, auth?.id, tenantId)
                .then(() => {
                    refreshTheReservation();
                    onClose();
                    handleTenantError(null);
                })
                .catch((error) => {
                    if (error?.response?.data?.includes("Declined")) {
                        refreshTheReservation();
                    }  
                    
                    setConfirmData({
                        title: 'Warning',
                        summary: error?.response?.data,
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        } else {
            onClose();
            setPleaseWait(false);
        }
    }

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullScreen={false}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320.591 320.591"
                    className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                    onClick={() => onClose()}
                >
                    <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                    <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                </svg>
                <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                    <div className="w-full mt-2">
                        <div className={`w-full`}>
                            <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                <div className="flex items-center gap-1.5">
                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Refund Transaction</h2>
                                </div>
                            </div>

                            <div className="mt-2 p-2">
                                <div className="w-full">
                                    <form onSubmit={handleSubmit}>
                                        <p className="text-[10px] mt-2 bg-[#fdf8e3] text-[#8a6d3b] text-left rounded-sm py-2 pl-4 pr-6">
                                            Once you click the "Refund" button the action can't be reversed, please review the amount before clicking on refund transaction
                                        </p>

                                        <div className="w-full mt-2">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount to refund</label>
                                                <input
                                                    type="number"
                                                    step={0.01}
                                                    min={0}
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={amount}
                                                    onChange={(e) =>
                                                        setAmount(validateMin(e.target.value, 0))
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-4 mt-6">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </Button>

                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.delete.bg
                                                }}
                                                type="submit"
                                            >
                                                Refund
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default SettledOutstandingBalance
